/* ============================================================
   HERCULES — lp-bundle-tiers.js

   Owns four jobs:
     1. Tier selection (radio semantics + keyboard)
     2. One-time / subscription mode switching
     3. Gift visibility — by mode, quantity and cart-value threshold
     4. Add to cart via /cart/add.js so the theme drawer opens

   Subscription prices are read from selling_plan_allocations, i.e.
   what Shopify will actually charge. One-time prices are calculated
   from the tier's discount percentage, which must match a real
   automatic discount. See the section file's header comment.
   ============================================================ */

(function () {
  'use strict';

  const MODE_ONETIME = 'onetime';
  const MODE_SUB = 'subscription';

  function parseJSONAttr(el, name, fallback) {
    const raw = el.getAttribute(name);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (err) {
      /* Warn rather than fail silently. A malformed data-variants attribute
         used to return {} here, which made selectVariant() bail on every click
         and the flavour selector appear to do nothing at all - with no error
         anywhere. Cause was an unquoted variant id used as a JSON object key. */
      console.warn('[lp-bundle-tiers] could not parse ' + name, err);
      return fallback;
    }
  }

  /* Mirrors Shopify's money filter for the common format strings,
     falling back to a plain dollar amount for unusual ones. */
  function formatMoney(cents, format) {
    const value = (Math.round(cents) / 100).toFixed(2);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1,');
    const withCents = dollars + '.' + parts[1];

    if (!format) return '$' + withCents;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          return dollars;
        default:
          return withCents;
      }
    });
  }

  class LpBundleTiers extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.moneyFormat = this.getAttribute('data-money-format') || '';
      this.unitPrice = parseInt(this.getAttribute('data-unit-price'), 10) || 0;
      this.variantId = this.getAttribute('data-variant-id');
      this.shipThreshold = parseInt(this.getAttribute('data-ship-threshold'), 10) || 0;
      this.needsPdp = this.getAttribute('data-needs-pdp') === 'true';
      this.productUrl = this.getAttribute('data-product-url') || '';
      this.hasPlans = this.getAttribute('data-has-plans') === 'true';

      this.planById = {};
      parseJSONAttr(this, 'data-plans', []).forEach((plan) => {
        this.planById[String(plan.id)] = plan;
      });

      /* Per-variant price + selling-plan allocations, so switching flavour
         recomputes subscription pricing rather than reusing the first
         variant's. Empty when the section is in 'auto' variant mode. */
      this.variantData = parseJSONAttr(this, 'data-variants', {}) || {};
      this.chips = Array.from(this.querySelectorAll('[data-lpbt-chip]'));

      this.tiers = Array.from(this.querySelectorAll('[data-lpbt-tier]'));
      this.modeButtons = Array.from(this.querySelectorAll('[data-lpbt-mode]'));
      this.atcButton = this.querySelector('[data-lpbt-atc]');
      this.atcPrice = this.querySelector('[data-lpbt-atc-price]');
      this.errorEl = this.querySelector('[data-lpbt-error]');
      this.shipEl = this.querySelector('[data-lpbt-ship]');
      this.subDisclaimer = this.querySelector('[data-lpbt-sub-disclaimer]');

      /* Cache the billing prefix before the first render overwrites it. */
      this.tiers.forEach((tier) => {
        const billing = tier.querySelector('[data-lpbt-billing]');
        if (billing && !billing.dataset.prefix) {
          billing.dataset.prefix = billing.textContent.trim().split(/\s+\$|\s+\d/)[0].trim();
        }
      });

      /* Default purchase mode. The landing-page section omits the attribute and
         keeps its original one-time default; lp-multi-tier-box sets
         data-default-mode="subscription". Falls back to one-time when the
         product has no selling plans, since setMode would refuse anyway. */
      const wantMode = this.getAttribute('data-default-mode');
      this.mode = wantMode === MODE_SUB && this.hasPlans ? MODE_SUB : MODE_ONETIME;

      /* The checked radio is the source of truth, not the CSS class. */
      const checked = this.querySelector('[data-lpbt-select]:checked');
      this.selected = (checked && checked.closest('[data-lpbt-tier]')) || this.tiers[0];
      if (this.selected) this.selected.classList.add('is-selected');

      this.initQuantityMode();
      this.initSimpleBundles();
      this.bind();
      this.render();
    }

    bind() {
      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.selectVariant(chip.getAttribute('data-variant-id'));
        });
      });

      this.tiers.forEach((tier) => {
        const input = tier.querySelector('[data-lpbt-select]');
        if (!input) return;

        /* Native radios give us grouping, arrow-key navigation and
           checked state for free — we only react to the change. */
        input.addEventListener('change', () => {
          if (input.checked) this.select(tier);
        });
      });

      this.modeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.setMode(button.getAttribute('data-lpbt-mode'));
        });
      });

      if (this.atcButton) {
        this.atcButton.addEventListener('click', () => this.addToCart());
      }
    }

    /* Switch flavour. Repoints unitPrice AND planById at the chosen variant,
       because subscription prices live on variant.selling_plan_allocations and
       differ per variant - reusing the old map would show one flavour's
       subscription price against another's variant id. */
    /* Normalise a selling plan id into the NUMERIC Shopify id that
       /cart/add.js requires.

       Three things get pasted into the tier settings, and only two are usable:
         4877418746                              plain Shopify id  - use as is
         gid://shopify/SellingPlan/4877418746    Shopify GID       - take digits
         74f01a74-0d2c-4466-970d-aea49b4c2156    SKIO internal id  - REJECT

       The Skio UUID is not a Shopify selling plan and cart/add.js cannot use
       it. It must be rejected outright: a naive "trailing digits" match pulls
       2156 out of that UUID, which looks like an id, is not one, and produces a
       silent non-subscription. Returning '' instead lets the caller fall back to
       the variant's own allocation, which is always valid.

       Number() on an unusable value gives NaN, JSON.stringify writes null, and
       cart/add.js then IGNORES selling_plan - the order completes as one-time at
       the subscription price with no error anywhere. Hence the strictness. */
    /* Simple Bundles support.

       A bundle's components are chosen through selects the app injects into
       the theme's product form, not through product.variants - so the normal
       flavour dropdown does not apply and the parent usually has a single
       variant. We render one labelled dropdown per component and write every
       choice back through SimpleBundlesAdapter, which keeps the select, the
       per-component hidden input and properties[_bundle_selection] in step.

       If there is no form (no Buy buttons block) or no selects, this is not a
       bundle and everything below is a no-op. */
    initSimpleBundles() {
      this.bundleMount = this.querySelector('[data-lpbt-bundle-components]');
      if (!this.bundleMount || typeof window.SimpleBundlesAdapter !== 'function') return;

      const formId = this.getAttribute('data-product-form-id');
      const form = formId ? document.getElementById(formId) : null;
      if (!form) return;

      /* ARRAY of maps, one per component, in the same order as the handles in
         custom.bundle_component_handles - which must match the order the
         components appear in the bundle. Indexed by position rather than a
         single flat title->image map, because Hercules flavour names repeat
         across products and a flat map would silently show one component's
         picture on another. Empty array = text-only rows. */
      this.componentImages = parseJSONAttr(this, 'data-component-images', []) || [];
      this.componentLabelMap = parseJSONAttr(this, 'data-component-labels', {}) || {};

      this.sbAdapter = new window.SimpleBundlesAdapter(form);
      this.sbAdapter.ready().then((components) => {
        if (!components.length) return;
        this.renderBundleComponents(components);
        this.bundleMount.hidden = false;
      });
    }

    /* Turn Simple Bundles' raw label into something a shopper can read.

       Raw looks like:
         "Pump of the Gods - Ultimate Non Stim Pre Workout Supplement - Flavour (select 1)"

       An exact override from custom.component_labels wins if present - that is
       the same metafield lp-bundle-buybox uses, so the two stay consistent.
       Otherwise strip the "(select 1)" suffix and a trailing
       "- Flavour" / "- Colour", which is what makes these unreadable. */
    componentHeading(rawLabel) {
      const overrides = this.componentLabelMap || {};
      if (overrides[rawLabel]) return overrides[rawLabel];

      return String(rawLabel || '')
        .replace(/\s*\((?:select|choose)[^)]*\)\s*$/i, '')
        .replace(/\s*[-–:]\s*(flavou?r|colou?r)\s*$/i, '')
        .trim();
    }

    /* Resolve a component's variant images WITHOUT any configuration.

       Simple Bundles gives us a label containing the component's product TITLE
       and option values that are variant titles - but nothing linking them to a
       product, so Liquid cannot build the map on its own. Two hops fix that:

         1. /search/suggest.json  title -> handle
         2. /products/<handle>.js handle -> variants with featured_image

       The suggest result is only accepted when its title matches the label we
       searched for, so a fuzzy match cannot silently attach the wrong product's
       pictures. Falls back to nothing, which renders text-only rows.

       custom.bundle_component_handles still wins when set - explicit beats
       inferred, and it skips both requests. */
    async resolveComponentImages(rawLabel) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const title = this.componentHeading(rawLabel);
      if (!title) return {};

      const norm = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();

      try {
        const suggestUrl =
          root + 'search/suggest.json?q=' + encodeURIComponent(title) +
          '&resources[type]=product&resources[limit]=5';
        const suggest = await fetch(suggestUrl, { headers: { Accept: 'application/json' } })
          .then((r) => r.json());

        const found =
          ((suggest.resources || {}).results || {}).products || [];
        if (!found.length) return {};

        /* Exact title match only. A near match is how you end up showing Energy
           of the Gods images on the Pump of the Gods picker. */
        const match = found.find((prod) => norm(prod.title) === norm(title));
        if (!match) {
          console.warn(
            '[lp-bundle-tiers] no exact product match for "' + title +
            '" - add its handle to custom.bundle_component_handles to fix.',
            { candidates: found.map((f) => f.title) }
          );
          return {};
        }

        const product = await fetch(root + 'products/' + match.handle + '.js', {
          headers: { Accept: 'application/json' }
        }).then((r) => r.json());

        const map = {};
        (product.variants || []).forEach((variant) => {
          const img =
            (variant.featured_image && variant.featured_image.src) ||
            product.featured_image ||
            '';
          if (img) map[variant.title] = img;
        });
        return map;
      } catch (err) {
        console.warn('[lp-bundle-tiers] could not resolve images for "' + title + '"', err);
        return {};
      }
    }

    renderBundleComponents(components) {
      const label = this.getAttribute('data-flavour-label') || 'Choose your flavour';
      const imageMaps = this.componentImages || [];
      this.bundleMount.innerHTML = '';

      components.forEach((component, index) => {
        const wrap = document.createElement('div');
        wrap.className = 'lpbt__step lpbt__bundle-component';

        /* Use the component's OWN label, not the generic setting. With four
           components a page of identical "Choose your flavour" headings tells
           the shopper nothing about which product each picker belongs to -
           Simple Bundles already phrases it usefully, e.g.
           "Energy of the Gods: Choose Flavour". Falls back to the setting only
           when the app gives us nothing. */
        const heading = document.createElement('p');
        heading.className = 'lpbt__step-title';
        heading.textContent = this.componentHeading(component.rawLabel) || label;
        wrap.appendChild(heading);

        /* Same details/summary dropdown as the standard PDP flavour picker, so
           bundles and single products look identical. Built in JS rather than
           Liquid because Simple Bundles injects its selects asynchronously and
           the option list is only known at runtime. */
        const dd = document.createElement('details');
        dd.className = 'lpbt__dd';

        const summary = document.createElement('summary');
        summary.className = 'lpbt__dd-current';

        const currentThumb = document.createElement('span');
        currentThumb.className = 'lpbt__dd-thumb';
        const currentImg = document.createElement('img');
        currentImg.loading = 'lazy';
        currentThumb.appendChild(currentImg);

        const currentName = document.createElement('span');
        currentName.className = 'lpbt__dd-name';

        summary.appendChild(currentThumb);
        summary.appendChild(currentName);
        summary.insertAdjacentHTML(
          'beforeend',
          '<svg class="lpbt__dd-chev" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
            '<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="2" ' +
            'stroke-linecap="round" stroke-linejoin="round"/></svg>'
        );
        dd.appendChild(summary);

        const panel = document.createElement('div');
        panel.className = 'lpbt__dd-panel';

        /* Position, not name: images[index] belongs to THIS component. */
        const images = imageMaps[index] || {};

        /* Look an option up by several keys. The map is keyed on VARIANT TITLE
           from Liquid, but Simple Bundles' option `value` is not guaranteed to
           be that title - it can carry the product name, extra whitespace or
           different casing, while the visible label reads correctly. So try
           value, then label, then a normalised form of each.

           Without this the pictures silently fail to resolve and every row
           renders text-only, which looks identical to "no metafield set". */
        const norm = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const normIndex = {};
        Object.keys(images).forEach((k) => { normIndex[norm(k)] = images[k]; });

        const imageFor = (opt) =>
          images[opt.value] ||
          images[opt.label] ||
          normIndex[norm(opt.value)] ||
          normIndex[norm(opt.label)] ||
          '';

        /* One warning per component listing exactly what was looked up against
           what is available - the fastest way to see whether the metafield is
           missing, out of order, or keyed differently. */
        if (Object.keys(images).length && !component.options.some(imageFor)) {
          console.warn(
            '[lp-bundle-tiers] image map for component ' + index + ' matched nothing.',
            { lookingFor: component.options.map((o) => o.value), available: Object.keys(images) }
          );
        }

        const paintCurrent = (value) => {
          currentName.textContent = value;
          const src = imageFor({ value: value, label: value });
          if (src) {
            currentImg.src = src;
            currentImg.alt = value;
            currentThumb.hidden = false;
          } else {
            /* No image for this option - hide the thumb rather than leave a
               broken or empty box. Happens when the component product is not
               listed in custom.bundle_component_handles. */
            currentThumb.hidden = true;
          }
        };

        component.options.forEach((opt) => {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'lpbt__dd-opt';
          if (opt.unavailable) {
            row.classList.add('is-soldout');
            row.disabled = true;
          }

          const thumb = document.createElement('span');
          thumb.className = 'lpbt__dd-thumb';
          const src = imageFor(opt);
          if (src) {
            const img = document.createElement('img');
            img.src = src;
            img.alt = opt.label;
            img.loading = 'lazy';
            thumb.appendChild(img);
          } else {
            thumb.hidden = true;
          }

          const name = document.createElement('span');
          name.className = 'lpbt__dd-opt-name';
          name.textContent = opt.label;

          row.appendChild(thumb);
          row.appendChild(name);

          if (opt.unavailable) {
            const soldOut = document.createElement('span');
            soldOut.className = 'lpbt__dd-opt-price';
            soldOut.textContent = 'Sold out';
            row.appendChild(soldOut);
          }

          row.addEventListener('click', () => {
            this.sbAdapter.select(component.rawLabel, opt.value);
            paintCurrent(opt.value);
            Array.from(panel.children).forEach((c) =>
              c.classList.toggle('is-selected', c === row)
            );
            dd.open = false;
          });

          if (this.sbAdapter.selections[component.rawLabel] === opt.value) {
            row.classList.add('is-selected');
          }
          panel.appendChild(row);
        });

        dd.appendChild(panel);
        wrap.appendChild(dd);
        this.bundleMount.appendChild(wrap);

        paintCurrent(this.sbAdapter.selections[component.rawLabel] || '');
        if (index === 0) this.bundleMount.hidden = false;

        /* Rows render immediately, text-only, then images drop in when the
           lookup returns - so a slow request never delays the picker being
           usable. Only runs when the metafield did not already supply a map. */
        if (Object.keys(images).length === 0) {
          this.resolveComponentImages(component.rawLabel).then((resolved) => {
            if (!resolved || !Object.keys(resolved).length) return;
            imageMaps[index] = resolved;
            Object.keys(resolved).forEach((k) => { normIndex[norm(k)] = resolved[k]; });
            Object.assign(images, resolved);

            Array.from(panel.children).forEach((row, i) => {
              const opt = component.options[i];
              if (!opt) return;
              const src = imageFor(opt);
              if (!src) return;
              const thumb = row.querySelector('.lpbt__dd-thumb');
              if (!thumb) return;
              thumb.innerHTML = '';
              const img = document.createElement('img');
              img.src = src;
              img.alt = opt.label;
              img.loading = 'lazy';
              thumb.appendChild(img);
              thumb.hidden = false;
            });

            paintCurrent(this.sbAdapter.selections[component.rawLabel] || '');
          });
        }
      });
    }

    /* Quantity mode. The stepper writes straight to the single tier's data-qty
       and re-renders, so pricing, gift unlocking, the subscribe toggle and add
       to cart all run through the same path as tiers - nothing below this
       method knows which layout is in use.

       No-op in tier mode: there is no stepper to bind. */
    initQuantityMode() {
      if (this.getAttribute('data-layout-mode') !== 'quantity') return;

      this.qtyMin = parseInt(this.getAttribute('data-qty-min'), 10) || 1;
      this.qtyMax = parseInt(this.getAttribute('data-qty-max'), 10) || 99;
      this.qtyValEl = this.querySelector('[data-lpbt-qty-val]');

      Array.from(this.querySelectorAll('[data-lpbt-qty-step]')).forEach((button) => {
        button.addEventListener('click', () => {
          this.stepQuantity(parseInt(button.getAttribute('data-lpbt-qty-step'), 10) || 0);
        });
      });

      this.syncQuantityButtons();
    }

    stepQuantity(delta) {
      const tier = this.selected;
      if (!tier) return;

      const current = parseInt(tier.getAttribute('data-qty'), 10) || 1;
      const next = Math.min(this.qtyMax, Math.max(this.qtyMin, current + delta));
      if (next === current) return;

      tier.setAttribute('data-qty', String(next));
      if (this.qtyValEl) this.qtyValEl.textContent = String(next);

      this.syncQuantityButtons();
      this.render();
    }

    /* Disable rather than hide at the bounds - a button that vanishes shifts
       the row and makes the other one move under the cursor. */
    syncQuantityButtons() {
      const tier = this.selected;
      if (!tier) return;
      const current = parseInt(tier.getAttribute('data-qty'), 10) || 1;

      Array.from(this.querySelectorAll('[data-lpbt-qty-step]')).forEach((button) => {
        const delta = parseInt(button.getAttribute('data-lpbt-qty-step'), 10) || 0;
        const next = current + delta;
        button.disabled = next < this.qtyMin || next > this.qtyMax;
      });
    }

    /* Singular or plural unit name.

       "tub" + "s" is wrong for a lot of words, and this now comes from a
       product metafield so it will meet bags, boxes, sachets and packs. Two
       ways to get it right:

         "box|boxes"   explicit, wins over everything
         "bag"         pluralised: +es after s/x/z/ch/sh, y -> ies, else +s

       Not a full pluraliser - it does not need to be. Anything irregular uses
       the explicit form. */
    unitWord(qty) {
      const raw = this.getAttribute('data-unit-label') || 'tub';

      if (raw.indexOf('|') > -1) {
        const parts = raw.split('|');
        return qty === 1 ? parts[0].trim() : parts[1].trim();
      }

      const word = raw.trim();
      if (qty === 1) return word;

      if (/(s|x|z|ch|sh)$/i.test(word)) return word + 'es';
      if (/[^aeiou]y$/i.test(word)) return word.slice(0, -1) + 'ies';
      return word + 's';
    }

    normalisePlanId(raw) {
      if (raw == null) return '';
      const value = String(raw).trim();
      if (value === '') return '';

      // Plain numeric id.
      if (/^\d+$/.test(value)) return value;

      // Shopify GID - anchored to the SellingPlan path so a GID for some other
      // resource type cannot slip through.
      const gid = value.match(/^gid:\/\/shopify\/SellingPlan\/(\d+)$/);
      if (gid) return gid[1];

      console.warn(
        '[lp-bundle-tiers] unusable selling plan id ' + JSON.stringify(value) +
        ' - use the number from gid://shopify/SellingPlan/NUMBER. ' +
        'Skio UUIDs are not Shopify selling plan ids. ' +
        'Falling back to this variant\'s own plan.'
      );
      return '';
    }

    selectVariant(id) {
      const data = this.variantData[String(id)];
      if (!data || data.available === false) return;

      this.variantId = String(id);
      this.unitPrice = data.price;

      this.planById = {};
      (data.plans || []).forEach((plan) => {
        this.planById[String(plan.id)] = plan;
      });

      let chosen = null;
      this.chips.forEach((chip) => {
        const on = chip.getAttribute('data-variant-id') === String(id);
        if (on) chosen = chip;
        chip.classList.toggle('is-selected', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      /* Dropdown variant UI (lp-multi-tier-box): copy the choice into the
         collapsed row and close the panel. No-op on surfaces that use text
         pills instead, so the landing-page section is unaffected. */
      const dd = this.querySelector('[data-lpbt-dd]');
      if (dd && chosen) {
        const nameEl = dd.querySelector('[data-lpbt-dd-name]');
        if (nameEl) nameEl.textContent = chosen.getAttribute('data-label') || '';

        const target = dd.querySelector('.lpbt__dd-current .lpbt__dd-thumb img');
        const source = chosen.querySelector('img');
        if (target && source) {
          /* currentSrc, not src: the option images are lazy-loaded, so src may
             still be the placeholder until the panel has been opened once. */
          target.src = source.currentSrc || source.src;
          target.removeAttribute('srcset');
        }
        dd.open = false;
      }

      this.syncGallery(data);
      this.render();
    }

    /* Drive the theme's product gallery from our own flavour picker.

       product-info.js owns this normally, but it reaches the gallery with
       this.querySelector from inside the info container - and this component
       is not part of that chain, so switching flavour here left the image
       untouched.

       media-gallery.js exposes setActiveMedia(variant) and only reads
       variant.featured_media.{id,position}, so a minimal object is enough. The
       gallery is a sibling of the buy box, hence document-level lookup. */
    syncGallery(data) {
      if (!data || !data.mediaId) return;

      const gallery = document.querySelector('[id^="MediaGallery-"]');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      try {
        if (typeof gallery.init === 'function') gallery.init();
        gallery.setActiveMedia({
          featured_media: { id: data.mediaId, position: data.mediaPos || 0 }
        });
      } catch (err) {
        /* Never let a gallery problem break variant selection - the price and
           add to cart still have to work. */
        console.warn('[lp-bundle-tiers] gallery sync failed', err);
      }
    }

    select(tier) {
      if (!tier) return;
      this.selected = tier;

      this.tiers.forEach((candidate) => {
        candidate.classList.toggle('is-selected', candidate === tier);
      });

      this.render();
    }

    setMode(mode) {
      if (mode !== MODE_SUB && mode !== MODE_ONETIME) return;
      if (mode === MODE_SUB && !this.hasPlans) return;

      this.mode = mode;

      this.modeButtons.forEach((button) => {
        const isOn = button.getAttribute('data-lpbt-mode') === mode;
        button.classList.toggle('is-active', isOn);
        button.setAttribute('aria-selected', isOn ? 'true' : 'false');
      });

      if (this.subDisclaimer) this.subDisclaimer.hidden = mode !== MODE_SUB;

      this.render();
    }

    /* Price for a tier in the current mode. Subscription totals come
       from the plan allocation; one-time totals are calculated. */
    priceFor(tier) {
      const qty = parseInt(tier.getAttribute('data-qty'), 10) || 1;
      /* RRP must scale with quantity. data-rrp-total is rendered by Liquid at
         first paint against the STARTING quantity and never changes, so in
         quantity mode the saving shrank as the shopper added units - $49.95 at
         one tub, $19.95 at two, because the total grew while the RRP did not.

         Recomputing from the per-unit RRP keeps the percentage constant and the
         dollar saving growing, which is the honest comparison and the one that
         rewards buying more. Falls back to the static attribute if data-rrp-unit
         is missing, so tier mode is unaffected either way. */
      const rrpUnit = parseInt(tier.getAttribute('data-rrp-unit'), 10) || 0;
      const rrpTotal = rrpUnit
        ? rrpUnit * qty
        : (parseInt(tier.getAttribute('data-rrp-total'), 10) || 0);
      let planId = this.normalisePlanId(tier.getAttribute('data-plan-id'));
      let total;

      /* A fixed price set on the tier overrides the percentage for that mode.
         Stored in cents; 0 means "not set", so a merchant never has to also
         flip a "pricing type" toggle and keep it in step with the value. */
      /* A fixed price means different things in the two layouts, and both are
         what a merchant would expect:

           tiers    - the price OF THAT TIER. "90-day supply = $199" is a
                      bundle price; multiplying it by 3 would be nonsense.
           quantity - the price PER UNIT, so the stepper actually changes the
                      total. Without this, setting $30 gave $30 whether the
                      shopper picked 1 or 12.

         Percentages scale in both modes because they are applied to
         unitPrice * qty. */
      const perUnitFixed = this.getAttribute('data-layout-mode') === 'quantity';
      const rawOnetime = parseInt(tier.getAttribute('data-onetime-price'), 10) || 0;
      const rawSub = parseInt(tier.getAttribute('data-sub-price'), 10) || 0;
      const fixedOnetime = perUnitFixed ? rawOnetime * qty : rawOnetime;
      const fixedSub = perUnitFixed ? rawSub * qty : rawSub;

      if (this.mode === MODE_SUB) {
        /* Resolve a plan that exists on the CURRENT variant before doing any
           subscription maths.

           data-plan-id is rendered server-side against whichever variant was
           selected at page load, so after a flavour switch it may not be one of
           this variant's allocations. Falling back to the variant's own first
           plan keeps subscribe working; leaving planId unresolved means
           addToCart sends no selling_plan and Shopify creates a ONE-TIME order
           at the subscription price. */
        if (!this.planById[planId]) {
          const firstPlan = Object.keys(this.planById)[0];
          planId = firstPlan || '';
        }

        /* A per-tier subscription % wins over the selling plan's own price.
           This exists because the real saving is often the plan discount PLUS a
           quantity automatic discount, which selling_plan_allocations does not
           know about - the allocation only reflects the plan.

           CAUTION: this is a DISPLAY figure. Shopify charges the plan price
           plus whatever discounts actually apply. If tierN_sub_pct does not
           equal plan + automatic discount, the price shown here will not match
           the cart. Set it to 0 to trust the allocation instead. */
        const subPct = parseInt(tier.getAttribute('data-sub-pct'), 10) || 0;

        if (fixedSub > 0) {
          total = fixedSub;
        } else if (subPct > 0) {
          total = Math.round((this.unitPrice * qty * (100 - subPct)) / 100);
        } else if (this.planById[planId]) {
          total = this.planById[planId].price * qty;
        } else {
          /* Subscribe selected but no plan resolved - fall back to the one-time
             maths rather than showing 0, and drop the plan so the cart is not
             sent a subscription it cannot honour. */
          const pct = parseInt(tier.getAttribute('data-onetime-pct'), 10) || 0;
          total = Math.round((this.unitPrice * qty * (100 - pct)) / 100);
          planId = '';
        }
      } else if (fixedOnetime > 0) {
        total = fixedOnetime;
        planId = '';
      } else {
        const pct = parseInt(tier.getAttribute('data-onetime-pct'), 10) || 0;
        total = Math.round((this.unitPrice * qty * (100 - pct)) / 100);
        planId = '';
      }

      return {
        qty,
        total,
        perMonth: Math.round(total / qty),
        rrpTotal,
        planId: this.mode === MODE_SUB ? planId : ''
      };
    }

    render() {
      /* Rebuilt on EVERY render pass. renderGifts() runs per tier and pushes
         only the selected tier's earned gifts, so without this reset the list
         accumulates on every quantity/mode change and the shopper would get
         duplicate gift lines. */
      this._earnedGifts = [];

      this.tiers.forEach((tier) => {
        const price = this.priceFor(tier);

        const isQtyMode = this.getAttribute('data-layout-mode') === 'quantity';

        /* Quantity mode shows what they will PAY. A per-unit "/mo" figure is
           meaningless on a one-off accessory, and it was the number people
           read first. */
        const priceEl = tier.querySelector('[data-lpbt-price]');
        if (priceEl) {
          priceEl.textContent = formatMoney(
            isQtyMode ? price.total : price.perMonth,
            this.moneyFormat
          );
        }

        const unitEl = tier.querySelector('[data-lpbt-price-unit]');
        if (unitEl) unitEl.hidden = isQtyMode;

        /* Mirror the active toggle's label into the card. Read from the button
           rather than a separate setting so there is one source of wording. */
        const modeNote = tier.querySelector('[data-lpbt-mode-note]');
        if (modeNote) {
          const active = this.modeButtons.find(
            (b) => b.getAttribute('data-lpbt-mode') === this.mode
          );
          const label = active
            ? (active.getAttribute('data-lpbt-mode-label') || active.textContent)
            : '';
          modeNote.textContent = label.replace(/\s+/g, ' ').trim();
        }

        const billingEl = tier.querySelector('[data-lpbt-billing]');
        if (billingEl) {
          const prefix = billingEl.dataset.prefix || 'Billed';
          const money = formatMoney(price.total, this.moneyFormat);
          /* Tub count belongs beside the billing figure - it is the unit the
             price is FOR, and without it "Billed $412.59 per delivery" gives no
             sense of what arrives. Singular/plural because "1 tubs" reads as a
             bug. The label is a setting so it can say sachets, bottles etc. */
          const units = price.qty + ' ' + this.unitWord(price.qty);

          if (isQtyMode) {
            /* The big number already states the cost, so this line carries the
               two things it cannot: how many, and how much is saved. */
            const saved = Math.max(0, price.rrpTotal - price.total);
            billingEl.textContent = saved > 0
              ? `${units} · Save ${formatMoney(saved, this.moneyFormat)}`
              : units;
          } else {
            billingEl.textContent =
              this.mode === MODE_SUB
                ? `${prefix} ${money} per delivery · ${units}`
                : `${prefix} ${money} once · ${units}`;
          }
        }

        const wasEl = tier.querySelector('[data-lpbt-was]');
        if (wasEl) {
          const showWas = price.rrpTotal > price.total;
          wasEl.hidden = !showWas;
          if (showWas) wasEl.textContent = formatMoney(price.rrpTotal, this.moneyFormat);
        }

        /* Banner text may contain {save}; fill it from the SAME figure the
           badge uses, so the headline and the badge can never disagree. The
           original text is cached because render() runs on every change and
           would otherwise substitute into its own output. */
        const bannerEl = tier.querySelector('[data-lpbt-banner]');
        if (bannerEl) {
          if (!bannerEl.dataset.tpl) bannerEl.dataset.tpl = bannerEl.textContent.trim();
          const pctOff = price.rrpTotal > price.total
            ? Math.round(((price.rrpTotal - price.total) / price.rrpTotal) * 100)
            : 0;
          bannerEl.textContent = bannerEl.dataset.tpl.replace(/\{save\}/gi, pctOff + '%');
          /* Hide a banner whose only content was a saving that does not exist,
             rather than printing "GET 0% OFF". */
          bannerEl.hidden = pctOff === 0 && /\{save\}/i.test(bannerEl.dataset.tpl);
        }

        const badgeEl = tier.querySelector('[data-lpbt-badge]');
        if (badgeEl) {
          if (price.rrpTotal > price.total) {
            /* Save format is a setting: percent, dollars, or both. Dollars read
               as more concrete on higher-priced tiers ("Save $52" beats "Save
               19%"), percent reads better on cheap ones - so it is a choice per
               product rather than a hardcoded format. Both figures come from
               rrpTotal vs total, so they can never disagree with the prices
               shown above them. */
            const saved = price.rrpTotal - price.total;
            const pctOff = Math.round((saved / price.rrpTotal) * 100);
            const money = formatMoney(saved, this.moneyFormat);
            const format = this.getAttribute('data-save-format') || 'percent';

            if (format === 'amount') {
              badgeEl.textContent = `Save ${money}`;
            } else if (format === 'both') {
              badgeEl.textContent = `Save ${money} (${pctOff}%)`;
            } else {
              badgeEl.textContent = `Save ${pctOff}%`;
            }
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        }

        /* Swap the feature list for the active purchase mode. Both lists are in
           the DOM; only one is shown. */
        Array.from(tier.querySelectorAll('[data-lpbt-features]')).forEach((list) => {
          list.hidden = list.getAttribute('data-lpbt-features') !== this.mode;
        });

        this.renderGifts(tier, price);
      });

      /* Afterpay instalment. lp-buybox.js writes this on its own block; the
         reassurance markup is shared, so this component has to fill it too or
         the amount stays at whatever Liquid rendered on load. */
      const afterpayEl = this.querySelector('[data-lp-afterpay-amount]');
      if (afterpayEl) {
        afterpayEl.textContent = formatMoney(
          Math.round(this.priceFor(this.selected).total / 4),
          this.moneyFormat
        );
      }

      const current = this.priceFor(this.selected);
      if (this.atcPrice) this.atcPrice.textContent = formatMoney(current.total, this.moneyFormat);
      this.renderShipping(current.total);
      this.clearError();
    }

    /* Both surfaces render a gift group inside each tier card, so gifts are
       always scoped to a supply:
         sections/lp-bundle-tiers.liquid  - money-shortfall status copy
         snippets/lp-multi-tier-box.liquid - plain-language copy, opted into
                                             with data-unlocked-label

       .lpbt__tier-body is display:none until .is-selected, so only the chosen
       supply's gifts are visible - no JS needed for the reveal.

       `record` says whether earned gifts from this scope should be queued for
       addToCart: true only for the SELECTED tier, so a shopper never receives
       the gifts belonging to a tier they did not buy. */
    renderGifts(tier, price) {
      this.renderGiftsIn(tier, price, tier === this.selected);
    }

    renderGiftsIn(scope, price, record) {
      Array.from(scope.querySelectorAll('[data-lpbt-gift-group]')).forEach((group) => {
        const type = group.getAttribute('data-lpbt-gift-group');
        let earnedValue = 0;   // summed value of the gifts actually unlocked

        /* A group carrying data-unlocked-label wants plain-language status copy
           in BOTH states ("Free - included" / "Add 2 more to unlock"), which is
           what lp-multi-tier-box renders. Groups without it keep the original
           money-shortfall behaviour, so the landing-page section is unchanged.
           Reading it off the group rather than a wrapper means the copy travels
           with the markup wherever it is placed. */
        const labels = group.hasAttribute('data-unlocked-label') ? group : null;

        /* Welcome kits and monthly gifts only exist on a subscription. */
        if ((type === 'welcome_kit' || type === 'monthly') && this.mode !== MODE_SUB) {
          group.hidden = true;
          return;
        }

        let anyVisible = false;

        Array.from(group.querySelectorAll('[data-lpbt-gift]')).forEach((gift) => {
          const modes = gift.getAttribute('data-modes') || 'both';
          const modeOk = modes === 'both' || modes === this.mode;

          /* A gift that belongs to the other purchase mode is REMOVED from the
             strip when the group uses money-shortfall copy, but in a group with
             plain-language labels it stays visible and LOCKED - same greyscale and lock badge as a
             quantity gift that has not been reached, with "Subscribe to unlock"
             as the reason. Showing what subscribing would unlock is the point of
             the row; hiding it just leaves a hole.

             This used to `return` here, which skipped the locked/earned logic
             below entirely, so the tile could never render in a locked state. */
          if (!modeOk && !labels) {
            gift.hidden = true;
            return;
          }

          const threshold = parseInt(gift.getAttribute('data-threshold'), 10) || 0;
          const minQty = parseInt(gift.getAttribute('data-min-qty'), 10) || 1;

          /* modeOk is part of `earned`, so a subscription gift is never counted
             as earned on one-time - which also keeps it out of _earnedGifts and
             therefore out of the cart. */
          const earned = modeOk && price.total >= threshold && price.qty >= minQty;

          gift.hidden = false;
          anyVisible = true;
          gift.classList.toggle('is-locked', !earned);

          /* Remember which gifts are actually earned on the CURRENTLY selected
             tier, so addToCart() can post them. renderGifts runs for every
             tier, so only record the selected one - otherwise a shopper would
             receive the gifts from tiers they did not buy. */
          if (earned) {
            /* Sum the value labels of unlocked gifts. Parsed from the label the
               merchant typed ("$39 value") rather than a separate numeric field,
               so the figure can never disagree with what is printed on the tile.
               Anything unparseable contributes 0 rather than breaking the sum. */
            const valueEl = gift.querySelector('.lp-gwp__value');
            if (valueEl) {
              const match = valueEl.textContent.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
              if (match) earnedValue += Math.round(parseFloat(match[1]) * 100);
            }
          }

          if (record && earned && gift.getAttribute('data-variant-id')) {
            this._earnedGifts.push({
              id: gift.getAttribute('data-variant-id'),
              name: gift.getAttribute('data-gift-name') || 'Gift',
              /* Stamped onto the cart line so the cart can re-check the gift
                 later. Without these the reconciler has no way to know WHY a
                 gift was given, and can only remove it when the cart empties
                 completely - so reducing tubs or dropping the subscription
                 would leave the gift behind. */
              minQty: parseInt(gift.getAttribute('data-min-qty'), 10) || 1,
              mode: gift.getAttribute('data-modes') || 'both',
              spend: parseInt(gift.getAttribute('data-threshold'), 10) || 0
            });
          }

          const lock = gift.querySelector('[data-lpbt-gift-lock]');
          if (lock) {
            if (labels) {
              /* Always visible, both states. A specific number converts better
                 than a bare "Locked". */
              lock.hidden = false;
              if (earned) {
                lock.textContent =
                  labels.getAttribute('data-unlocked-label') || 'Free - included';
              } else if (modes === 'subscription') {
                lock.textContent =
                  labels.getAttribute('data-sub-label') || 'Subscribe to unlock';
              } else if (threshold > 0) {
                /* Spend gift. Measured against the SELECTED SUPPLY's total,
                   not the whole cart - the shopper is being shown what this
                   choice unlocks. cart-drawer.js re-checks against the real
                   cart total afterwards, which is only ever more generous. */
                const short = Math.max(threshold - price.total, 0);
                lock.textContent = short > 0
                  ? 'Spend ' + formatMoney(short, this.moneyFormat) + ' more to unlock'
                  : 'Locked';
              } else {
                const need = Math.max(minQty - price.qty, 0);
                lock.textContent = need > 0
                  ? 'Add ' + need + ' more to unlock'
                  : 'Locked';
              }
            } else if (earned) {
              lock.hidden = true;
            } else {
              lock.hidden = false;
              const shortfall = Math.max(threshold - price.total, 0);
              lock.textContent =
                shortfall > 0 ? '+' + formatMoney(shortfall, this.moneyFormat) : 'Locked';
            }
          }
        });

        /* Hides an empty row. With gifts split into a standard row and a
           subscription row, a product with no subscription gifts renders an
           empty second group - heading and all - unless it is hidden here.
           anyVisible is false only when the group produced no tiles. */
        /* Write the unlocked total into this group's heading. Empty when nothing
           is unlocked, so a locked row does not advertise a value. */
        const totalEl = group.querySelector('[data-lpbt-gift-total]');
        if (totalEl) {
          totalEl.textContent = earnedValue > 0
            ? formatMoney(earnedValue, this.moneyFormat)
            : '';
          totalEl.hidden = earnedValue <= 0;
        }

        group.hidden = !anyVisible;
      });
    }

    renderShipping(total) {
      if (!this.shipEl || !this.shipThreshold) return;

      this.shipEl.hidden = false;
      this.shipEl.textContent =
        total >= this.shipThreshold
          ? 'Free shipping unlocked'
          : `Add ${formatMoney(this.shipThreshold - total, this.moneyFormat)} for free shipping`;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    addToCart() {
      /* needsPdp is the legacy escape hatch for bundles this component could
         not build a payload for. It no longer applies when the Simple Bundles
         adapter has components: we post the form and every property travels
         with it. Still honoured otherwise, so the landing-page section keeps
         linking out for products it genuinely cannot add. */
      if (this.needsPdp && !(this.sbAdapter && this.sbAdapter.components.length)) {
        window.location.href = this.productUrl;
        return;
      }

      if (!this.variantId) {
        this.showError('This bundle is unavailable right now.');
        return;
      }

      const price = this.priceFor(this.selected);

      /* Never silently sell a one-time order at a subscription price. If
         Subscribe is chosen but no selling plan resolved, that is a config
         problem (missing plan IDs on the tier, or a variant with no
         allocations) and it must surface rather than quietly charge wrong. */
      if (this.mode === MODE_SUB && !price.planId) {
        this.showError('Subscription is unavailable for this option. Please choose one-time purchase.');
        console.warn('[lp-bundle-tiers] subscribe selected but no selling plan resolved for variant', this.variantId);
        return;
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      /* Simple Bundles: post the FORM, not JSON.

         Every hidden input the app maintains has to travel with the add -
         properties[_bundle_selection] and one properties[Component] each - and
         component labels contain characters that do not survive a JSON round
         trip. FormData carries the lot and /cart/add.js accepts it directly.
         The buy box only overrides id, quantity and selling_plan. */
      const isBundle = !!(this.sbAdapter && this.sbAdapter.components.length);
      const body = isBundle
        ? this.sbAdapter.formData(this.variantId, price.qty, price.planId)
        : JSON.stringify({
            items: [
              Object.assign(
                { id: Number(this.variantId), quantity: price.qty },
                price.planId ? { selling_plan: Number(price.planId) } : {}
              )
            ]
          });
      // FormData must NOT carry a Content-Type header - the browser sets the
      // multipart boundary itself.
      const headers = isBundle
        ? { Accept: 'application/json' }
        : { 'Content-Type': 'application/json', Accept: 'application/json' };


      this.clearError();
      if (this.atcButton) this.atcButton.setAttribute('aria-busy', 'true');

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: headers,
        body: body
      })
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok) {
              throw new Error(data.description || data.message || 'Add to cart failed');
            }
            return data;
          })
        )
        .then(() => {
          /* Gifts go in only AFTER Shopify accepts the bundle line, so a failed
             add can never leave orphan gift lines. Each is posted separately and
             serialised: cart-drawer.js patches window.fetch to refresh on every
             mutation, so parallel writes thrash the drawer.

             `_lp_gift` is REQUIRED. handleGWP() in cart-drawer.js checks
             isLpGiftItem() and exempts these lines from the $150/$200 spend
             sweep. Without the flag the cart deletes them on next refresh.

             Failures are swallowed on purpose - a misconfigured or unpublished
             gift must never surface an error over a successful purchase. */
          var gifts = (this._earnedGifts || []).slice();
          var addUrl = root + 'cart/add.js';
          var productId = this.getAttribute('data-product-id') || '';

          /* Gifts are ONCE PER CART. Read the cart first and drop any gift
             already in it, otherwise a second add-to-cart posts them all again
             and Shopify creates SEPARATE lines - it only merges lines whose
             properties match exactly, and _lp_gift_min / _lp_gift_pid differ by
             tier. That is why adding 3 tubs, leaving, then adding 3 more gave
             two of every gift.

             cart-drawer.js also sweeps duplicates as a safety net, but not
             making the request beats making it and undoing it. */
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .catch(function () { return { items: [] }; })
            .then(function (cart) {
              var already = {};
              (cart.items || []).forEach(function (i) {
                if (i.properties && i.properties._lp_gift) {
                  already[String(i.variant_id)] = true;
                }
              });
              gifts = gifts.filter(function (g) { return !already[String(g.id)]; });
              if (!gifts.length) return;
              return gifts.reduce(function (chain, g) {
            return chain.then(function () {
              return fetch(addUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                  items: [{
                    id: Number(g.id),
                    quantity: 1,
                    /* Underscore-prefixed properties are hidden from the
                       customer in cart, checkout and order confirmations. */
                    properties: {
                      _lp_gift: g.name,
                      _lp_gift_min: String(g.minQty),
                      _lp_gift_mode: g.mode,
                      _lp_gift_spend: String(g.spend),
                      _lp_gift_pid: String(productId)
                    }
                  }]
                })
              }).catch(function () { /* best effort */ });
            });
              }, Promise.resolve());
            });
        })
        .then(() => {
          document.dispatchEvent(new CustomEvent('lpbt:added', { bubbles: true }));
        })
        .catch((err) => {
          this.showError(err.message || 'Something went wrong. Please try again.');
        })
        .finally(() => {
          if (this.atcButton) this.atcButton.removeAttribute('aria-busy');
        });
    }
  }

  if (!window.customElements.get('lp-bundle-tiers')) {
    window.customElements.define('lp-bundle-tiers', LpBundleTiers);
  }
})();